import { Component, inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialog } from '@angular/material/dialog';
import { UploadedImageShot } from 'src/app/components/photographer/models/new/imageShot';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { SnackbarType } from 'src/app/shared/components/snackbar/snackbar.component';
import { Selectable } from 'src/app/shared/types/selectable.type';
import { UploadMultipleModalComponent } from '../upload-multiple-modal/upload-multiple-modal.component';
import { environment } from 'src/environments/environment';
import { ExcelService } from 'src/app/services/excel.service';
import { CommonService } from 'src/app/services/common.service';
import { HttpEventType } from '@angular/common/http';
import { OrderStatus } from 'src/app/components/company/models/new/order';
import { MutationQuery } from 'src/app/shared/types/mutation-query.enum';
import { GraphQLService } from 'src/app/services/graph-ql.service';
import { EstimatedNameModalComponent } from './estimated-name-modal/estimated-name-modal.component';

@Component({
  selector: 'bt-upload-multiple-with-orchestrator-modal',
  standalone: false,
  templateUrl: './upload-multiple-with-orchestrator-modal.component.html',
  styleUrl: './upload-multiple-with-orchestrator-modal.component.scss'
})
export class UploadMultipleWithOrchestratorModalComponent {
  readonly #dialogRef = inject(MatDialogRef<UploadMultipleWithOrchestratorModalComponent>);
  readonly data = inject<any>(MAT_DIALOG_DATA);
  loading = false;
  isDragging = false;
  public environment = environment;
  uploadProgress = 0;
  uploading = false;

  constructor(
    private snackbar: UtilitiesService,
    private excelService: ExcelService,
    private commonService: CommonService,
    private graphQLService: GraphQLService,
    private dialog: MatDialog
  ) {}

  onFilesSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    if (!input.files || !input.files.length) return;

    this.handleFiles(input.files);
  }

  onDragOver(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent) {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;

    if (!event.dataTransfer?.files?.length) return;

    this.handleFiles(event.dataTransfer.files);
  }

  openEstimatedNameModal() {
    this.dialog.open(EstimatedNameModalComponent, {
      width: '400px',
      data: {}
    });
  }

  copyFilenamesToClipboard() {
    const filenames = this.data.estimatedCodes.join('\n');
    navigator.clipboard.writeText(filenames).then(() => {
      this.snackbar.showSnackbar('Filenames copied to clipboard', SnackbarType.Success);
    });
  }

  copyCodeToClipboard(code: string) {
    navigator.clipboard.writeText(code).then(() => {
      this.snackbar.showSnackbar('Code copied to clipboard', SnackbarType.Success);
    });
  }

  downloadFilenamesExcel() {
    this.loading = true;

    if (this.data.order) {
      this.excelService.downloadFilenamesExcel(this.data.order.id).subscribe({
        next: (res: Blob) => {
          this.loading = false;
          const url = window.URL.createObjectURL(res);
          const a = document.createElement('a');
          a.href = url;
          a.download = `Filenames_Order_${this.data.order?.id}.xlsx`;
          a.click();
          window.URL.revokeObjectURL(url);
        },
        error: (err: any) => {
          this.loading = false;
          this.snackbar.showSnackbar('Failed to export excel', SnackbarType.Error);
        }
      });
    } else {
      this.loading = true;
      this.excelService.getImageList(this.data.style?.modelId).subscribe({
        next: (res: Blob) => {
          this.loading = false;
          const url = window.URL.createObjectURL(res);
          const a = document.createElement('a');
          a.href = url;
          a.download = `Images_model_${this.data.style?.modelId}.xlsx`;
          a.click();
          window.URL.revokeObjectURL(url);
          this.snackbar.showSnackbar('Excel exported successfully', SnackbarType.Success);
        },
        error: (err) => {
          this.loading = false;
          this.snackbar.showSnackbar('Failed to export excel', SnackbarType.Error);
        }
      });
    }
  }

  setOrderInProgress() {
    let payload = {
      orderId: this.data.order?.id,
      orderStatusId: OrderStatus.InProgress
    };

    return this.graphQLService.postData(MutationQuery.UPDATE_ORDER_STATUS, { updateOrderStatusInput: payload });
  }

  uploadZipToBlobStorage(formData: FormData) {
    this.commonService.uploadZipToBlobStorage(formData).subscribe({
      next: (event) => {
        switch (event.type) {
          case HttpEventType.Sent:
            this.uploadProgress = 0;
            break;

          case HttpEventType.UploadProgress:
            if (event.total) {
              this.uploadProgress = Math.round((100 * event.loaded) / event.total);
            }
            break;

          case HttpEventType.Response:
            this.uploading = false;
            this.uploadProgress = 100;

            this.#dialogRef.close({
              instanceId: event.body[0]
            });

            break;
        }
      },

      error: () => {
        this.uploading = false;
        this.uploadProgress = 0;
        this.snackbar.showSnackbar('Failed to upload files', SnackbarType.Error);
      }
    });
  }

  handleFiles(files: FileList) {
    this.uploading = true;

    for (const file of Array.from(files)) {
      if (file.size > this.environment.maxFileSize * 1024 * 1024) {
        this.snackbar.showSnackbar(`File size exceeds ${this.environment.maxFileSize}MB.`, SnackbarType.Error);
        this.uploading = false;
        return;
      }
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      if (fileExtension !== 'zip') {
        this.snackbar.showSnackbar('Only ZIP files are allowed.', SnackbarType.Error);
        this.uploading = false;
        return;
      }
    }

    const formData = new FormData();
    for (const file of Array.from(files)) {
      formData.append('files', file);
    }

    if (this.data.order?.id) {
      formData.append('orderId', this.data.order.id.toString());
      this.setOrderInProgress().subscribe({
        next: () => {
          this.uploadZipToBlobStorage(formData);
        },
        error: (err) => {
          this.uploading = false;
          this.snackbar.showSnackbar('Failed to set order in progress', SnackbarType.Error);
        }
      });
    } else if (this.data.style?.modelId) {
      formData.append('modelId', this.data.style.modelId.toString());

      this.uploadZipToBlobStorage(formData);
    }
  }

  close() {
    this.#dialogRef.close();
  }
}
